/**
 * The Copilot's blank-conversation hero: a soft cardinal→teal orb, a one-line greeting + explainer, and
 * the example-question chips — vertically centered so the chips sit near the composer instead of pinned
 * to the top of the panel. The orb is pure CSS (conic gradient + blur, no WebGL); motion is gated off
 * under prefers-reduced-motion.
 */
import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useReducedMotion } from 'framer-motion';
import { tokens } from './tokens';
import { EXAMPLE_QUESTIONS } from './demoCache';

// An all-cardinal orb — Stanford Cardinal in dark→light→rose shades for depth (no cool/teal stop).
const CARDINAL_DARK = '#5C0E0E';
const CARDINAL = tokens.brand.cardinal; // #8C1515
const CARDINAL_LIGHT = '#B33A3A';
const CARDINAL_ROSE = '#D06A6A'; // soft rose highlight

const spin = keyframes`to { transform: rotate(360deg); }`;
const breathe = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.04); }
`;

// The orb: a blurred conic gradient (cardinal → teal → blue → cardinal) slowly rotating inside a circle,
// with a glass sheen on top and a soft ambient glow behind. `data-reduce` stops all motion.
const Orb = styled.div`
  position: relative;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  &[data-reduce='false'] {
    animation: ${breathe} 6s ease-in-out infinite;
  }
  &::after {
    /* ambient glow */
    content: '';
    position: absolute;
    inset: -22%;
    border-radius: 50%;
    background: radial-gradient(circle, ${tokens.brand.cardinal}22 0%, transparent 70%);
    z-index: -1;
  }
`;

const OrbCore = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: inset 0 1px 6px rgba(15, 23, 42, 0.14), 0 8px 22px rgba(140, 21, 21, 0.18);
  &::before {
    content: '';
    position: absolute;
    inset: -28%;
    background: conic-gradient(
      from 0deg,
      ${CARDINAL_DARK},
      ${CARDINAL} 28%,
      ${CARDINAL_LIGHT} 52%,
      ${CARDINAL_ROSE} 70%,
      ${CARDINAL_DARK} 100%
    );
    filter: blur(9px) saturate(1.1);
  }
  &[data-reduce='false']::before {
    animation: ${spin} 16s linear infinite;
  }
  &::after {
    /* glass sheen */
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: radial-gradient(
      circle at 38% 30%,
      rgba(255, 255, 255, 0.6) 0%,
      rgba(255, 255, 255, 0.12) 26%,
      transparent 56%
    );
  }
`;

export function EmptyState({ onExample }: { onExample: (q: string) => void }) {
  const reduce = (useReducedMotion() ?? false).toString() as 'true' | 'false';
  return (
    <Box
      sx={{
        flex: 1,
        width: '100%',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 2,
        px: 3,
        py: 4,
      }}
    >
      <Orb data-reduce={reduce}>
        <OrbCore data-reduce={reduce} />
      </Orb>

      <Box>
        <Typography
          sx={{ fontFamily: tokens.fontFamily, fontSize: 22, fontWeight: 700, color: tokens.text.primary, letterSpacing: '-0.01em' }}
        >
          StudySync Copilot
        </Typography>
        <Typography
          sx={{
            fontFamily: tokens.fontFamily,
            fontSize: 14.5,
            color: tokens.text.secondary,
            lineHeight: 1.55,
            maxWidth: 440,
            mx: 'auto',
            mt: 0.75,
          }}
        >
          Ask about this study’s data in plain language. I’ll pull the numbers, chart them, and show you
          exactly what was queried.
        </Typography>
      </Box>

      <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1} sx={{ maxWidth: 620, mt: 0.5 }}>
        {EXAMPLE_QUESTIONS.map((q) => (
          <Chip
            key={q}
            label={q}
            onClick={() => onExample(q)}
            variant="outlined"
            sx={{
              fontFamily: tokens.fontFamily,
              borderColor: tokens.surface.border,
              color: tokens.text.secondary,
              bgcolor: tokens.surface.card,
              '&:hover': { borderColor: tokens.brand.cardinal, color: tokens.text.primary },
              '&:focus-visible': { outline: `2px solid ${tokens.brand.cardinal}`, outlineOffset: '2px' },
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}
